import { DateTime } from 'luxon';
import { SECTION_ALIAS } from '../constants/OneRealMortgageConstants';
import {
  AnswerApplicationQuestionRequest,
  ApplicationStep,
  FieldObject,
  FieldObjectTypeEnum,
  Question,
} from '../openapi/atlantis';
import { SectionProgressDto } from '../openapi/yenta';
import { ISelectOption } from '../types';
import {
  formatAddress,
  getAddressFromAddressComponent,
} from './GoogleMapUtils';
import { getBooleanParse } from './TypeUtils';

export enum NextActionType {
  INITIATE = 'INITIATE',
  BUTTON_ACTION = 'BUTTON_ACTION',
}

export enum DisplayTypeInputEnum {
  SINGLE_SELECT_IMAGE = 'SINGLE_SELECT_IMAGE',
  SINGLE_SELECT_DROP_DOWN = 'SINGLE_SELECT_DROP_DOWN',
  SINGLE_SELECT_IMAGE_DESCRIPTION = 'SINGLE_SELECT_IMAGE_DESCRIPTION',
  SINGLE_FILE = 'SINGLE_FILE',
  CONSENT_CHECKBOX = 'CONSENT_CHECKBOX',
  POSITIVE_NUMBER = 'POSITIVE_NUMBER',
  SINGLE_SELECT_BUTTONS = 'SINGLE_SELECT_BUTTONS',
}

export interface ApplicationStepWithNewContent<T>
  extends Omit<ApplicationStep, 'content'> {
  id?: string | undefined;
  content: T;
  alias?: string | null;
}

export const isEmptyAnswer = (answer: any): boolean => {
  if (answer === null || answer === undefined || answer === '') {
    return true;
  }

  return false;
};

// this function returns the answer for a address field
export const getAddressFieldAnswer = (answer: Record<string, any>) => {
  if (!answer) {
    return;
  }

  if (answer?.isManualAddress) {
    return {
      type: answer?.type || null,
      country: 'UNITED_STATES',
      streetAddress1: answer?.streetAddress1 || null,
      streetAddress2: answer?.streetAddress2 || null,
      city: answer?.city || null,
      county: answer?.county || null,
      stateOrProvince: answer?.stateOrProvince,
      zipOrPostalCode: answer?.zipOrPostalCode,
    };
  }

  if (answer?.place_id) {
    return getAddressFromAddressComponent(answer?.address_components, [
      'streetAddress2',
    ]);
  }

  return answer;
};

// this function returns the answer for a Select field
export const getSelectFieldAnswer = (
  field: FieldObject,
  answer: ISelectOption[],
  isTCOnboarding: boolean = false,
) => {
  const displayType = field.fieldStyle?.displayType;

  if (displayType === 'dropdown') {
    if (isTCOnboarding) {
      return answer.map((option) => option);
    }
    return answer.map((option) => option.value);
  }

  return answer;
};

//this function returns the answer for a field based on its type
export const getFieldAnswer = (
  field: FieldObject,
  answer: any,
  isTCOnboarding: boolean = false,
) => {
  if (isEmptyAnswer(answer)) {
    if (answer === '') {
      return null;
    }

    return answer;
  }

  switch (field.type) {
    case FieldObjectTypeEnum.Address:
      return getAddressFieldAnswer(answer);

    case FieldObjectTypeEnum.MultiSelect:
      return getSelectFieldAnswer(field, answer, isTCOnboarding);

    case FieldObjectTypeEnum.FileRequest:
    case FieldObjectTypeEnum.Binary:
    case FieldObjectTypeEnum.Toggle:
      return getBooleanParse(answer);

    case FieldObjectTypeEnum.Number:
      return Number(answer);

    case FieldObjectTypeEnum.Money:
      return { amount: answer || 0, currency: 'USD' };

    case FieldObjectTypeEnum.Date:
      if (!answer) return null;

      // Handle both string and Date object inputs
      if (typeof answer === 'string') {
        // Already in LocalDate format, return as-is
        return answer;
      }

      // If it's a Date object, extract local date components (no timezone conversion)
      if (answer instanceof Date) {
        const year = answer.getFullYear();
        const month = String(answer.getMonth() + 1).padStart(2, '0');
        const day = String(answer.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      return answer;

    default:
      return answer;
  }
};

// this function returns the answer for a field array
export const getFieldArrayAnswer = (
  field: FieldObject,
  fieldAnswer: any[],
  isTCOnboarding: boolean = false,
) => {
  const fieldArrayAnswer = (fieldAnswer || []).map((fieldValue: any) => {
    //@ts-ignore
    return (field.fields || []).map((childField: FieldObject) => {
      const value = fieldValue[childField.id!];

      return {
        fieldId: childField.id,
        answerType: childField.answerType,
        answer: getFieldAnswer(childField, value, isTCOnboarding),
      };
    });
  });

  const answer = {
    ...field,
    answer: fieldArrayAnswer,
  };

  return [answer];
};

// this function returns the answers for fields in a form.
export const getFieldsAnswers = (
  fields: FieldObject[],
  formData: Record<string, any>,
  isTCOnboarding: boolean = false,
) => {
  return (fields || []).flatMap((field: FieldObject) => {
    if (!formData.hasOwnProperty(field.id!)) {
      return [];
    }

    const fieldAnswer = formData[field.id!];

    if (field.type === FieldObjectTypeEnum.FieldArray) {
      return getFieldArrayAnswer(field, fieldAnswer, isTCOnboarding);
    }

    const answer = {
      ...field,
      answer: getFieldAnswer(field, fieldAnswer, isTCOnboarding),
    };

    return [answer];
  });
};

// Helper function to get default answer for special fields based on question context
export const getDefaultAnswerForSpecialFields = (
  questionAlias: string | null | undefined,
  fieldType: FieldObjectTypeEnum | any, // Accept any to handle custom field types like 'PAYMENT_REQUEST'
): string | null => {
  // Early return for invalid inputs
  if (!questionAlias || !fieldType) {
    return null;
  }

  // Define field-specific default answers
  const fieldDefaults: Record<string, Record<string, string>> = {
    [FieldObjectTypeEnum.Text]: {
      [SECTION_ALIAS.REVIEW_APPLICATION]: 'reviewed',
    },
    PAYMENT_REQUEST: {
      [SECTION_ALIAS.APPLICATION_FEE]: 'paid',
    },
  };

  // Get the default answer for the specific field type and question alias
  return fieldDefaults[fieldType]?.[questionAlias] ?? null;
};

// Helper function to populate special fields in form data
export const populateSpecialFieldsInFormData = (
  formData: Record<string, any>,
  fields: FieldObject[],
  questionAlias?: string | null,
): void => {
  fields.forEach((field) => {
    if (field.id && field.type && !formData[field.id]) {
      const defaultAnswer = getDefaultAnswerForSpecialFields(
        questionAlias,
        field.type,
      );
      if (defaultAnswer) {
        formData[field.id] = defaultAnswer;
      }
    }
  });
};

//this function returns the answer for a question form
export const getFormQuestionAnswers = (
  config: ApplicationStepWithNewContent<Question>,
  formData: Record<string, any>,
  isTCOnboarding: boolean = false,
): AnswerApplicationQuestionRequest => {
  const fields = [
    ...(config.content.fields || []),
    ...((config.content.segments || []).flatMap((s) => s.fields) || []),
  ];

  // Populate special fields in formData if they're missing
  populateSpecialFieldsInFormData(formData, fields, config?.alias);

  const processedFields = getFieldsAnswers(fields, formData, isTCOnboarding);

  const answers = (processedFields || []).map((field) => ({
    fieldId: field.id,
    answer: field.answer,
    answerType: field.answerType,
  }));

  return {
    questionId: config.content.id!,
    //@ts-ignore
    answers,
  };
};

// This function returns the default values for an address field.
export const getAddressFieldDefaultValue = (
  address: any,
): Record<string, any> | undefined => {
  if (!address) {
    return;
  }

  return {
    formatted_address: formatAddress(address),
    country: address?.country,
    streetAddress1: address?.streetAddress1,
    streetAddress2: address?.streetAddress2,
    city: address?.city,
    county: address?.county,
    stateOrProvince: address?.stateOrProvince,
    zipOrPostalCode: address?.zipOrPostalCode,
  };
};

// This function returns the default values for select field.
export const getSelectFieldDefaultValue = (
  field: FieldObject,
  answer: any,
  isTCOnboarding: boolean = false,
): Record<string, any> | undefined => {
  if (!answer) {
    return;
  }

  const displayType = field.fieldStyle?.displayType;

  if (displayType === 'dropdown') {
    //@ts-ignore
    const options = field.options || [];
    if (isTCOnboarding) {
      return options
        ?.filter((option: { value: string }) => answer?.includes(option?.value))
        ?.map((option: { value: string }) => option?.value);
    }

    return options?.filter(
      (option: { value: string }) => answer?.includes(option?.value),
    );
  }

  return answer;
};

// This function returns the default values for a specified field based on its type.
export const getFieldDefaultValues = (
  field: FieldObject,
  answer: any,
  isTCOnboarding: boolean = false,
): any => {
  switch (field.type) {
    case FieldObjectTypeEnum.Address:
      return getAddressFieldDefaultValue(answer);

    case FieldObjectTypeEnum.MultiSelect:
      return getSelectFieldDefaultValue(field, answer, isTCOnboarding);

    case FieldObjectTypeEnum.Number:
      return answer?.toString();

    case FieldObjectTypeEnum.Money:
      return answer?.amount?.toString();

    case FieldObjectTypeEnum.Toggle:
      // For TC Onboarding, ensure we return a boolean value
      if (isTCOnboarding) {
        if (answer !== null && answer !== undefined) {
          return answer === true || answer === 'true';
        }
        return field?.presetAnswer === true || field?.presetAnswer === 'true';
      }
      // For non-TC onboarding, keep string format for ControlledToggleInput
      return answer?.toString() ?? field?.presetAnswer?.toString();

    case FieldObjectTypeEnum.Binary:
      return answer?.toString();

    case FieldObjectTypeEnum.FileRequest:
      return answer?.toString();

    case FieldObjectTypeEnum.UrlRequest:
      return true;

    case FieldObjectTypeEnum.Date:
      if (isTCOnboarding) {
        return DateTime.fromISO(answer).isValid
          ? DateTime.fromISO(answer)
              .setZone('local', { keepLocalTime: true })
              .toJSDate()
          : undefined;
      }

      return answer;

    default:
      return answer;
  }
};

// This function returns an default values for a field array.
export const getFieldArrayDefaultValues = (
  fields: FieldObject[],
  fieldAnswer: any[],
  isTCOnboarding: boolean = false,
): Record<string, any> => {
  const fieldArrayDefaultValues: Record<string, any> = {};

  (fieldAnswer || []).forEach(({ fieldId, answer }) => {
    const field = (fields || []).find((f) => f.id === fieldId);
    if (field) {
      const defaultValue = getFieldDefaultValues(field, answer, isTCOnboarding);
      fieldArrayDefaultValues[fieldId] = defaultValue;
    }
  });

  return fieldArrayDefaultValues;
};

// This function sets the default values for fields in a form or segment.
export const getFieldsDefaultValues = (
  fields: FieldObject[] = [],
  answered: boolean,
  isTCOnboarding: boolean = false,
): Record<string, any> => {
  const fieldsDefaultValues: Record<string, any> = {};

  (fields || []).forEach((field) => {
    const { id, type, answer, presetAnswer } = field;
    const fieldAnswer = answered ? answer : presetAnswer;

    switch (type) {
      case FieldObjectTypeEnum.FieldArray:
        const fieldArrayAnswers = fieldAnswer?.map((ans: any) => {
          //@ts-ignore
          return getFieldArrayDefaultValues(field.fields, ans, isTCOnboarding);
        });
        fieldsDefaultValues[id!] = fieldArrayAnswers;
        break;
      default:
        const defaultAnswer = getFieldDefaultValues(
          field,
          fieldAnswer,
          isTCOnboarding,
        );
        fieldsDefaultValues[id!] = defaultAnswer;
        break;
    }
  });

  return fieldsDefaultValues;
};

// This function returns the default values for a form, given its configuration.
export const getFormDefaultValues = (
  config: ApplicationStepWithNewContent<Question>,
  isTCOnboarding: boolean = false,
): Record<string, any> => {
  const formDefaultValues = Object.assign(
    {},
    getFieldsDefaultValues(
      config.content.fields,
      config.content.answered!,
      isTCOnboarding,
    ),
    ...(config.content.segments || []).map((segment) =>
      getFieldsDefaultValues(
        segment.fields,
        config.content.answered!,
        isTCOnboarding,
      ),
    ),
  );

  return formDefaultValues;
};

// this function returns the id of the previous question in the application
export const getPreviousQuestionId = (
  sectionProgresses: Array<SectionProgressDto>,
  questionId?: string,
) => {
  const allQuestions = (sectionProgresses || []).flatMap(
    (section) => section.questionProgresses,
  );

  const currentQuestionIndex = (allQuestions || []).findIndex(
    (question) => question?.id === questionId,
  );

  // No previous question found or the current question is the first one
  if (currentQuestionIndex < 1) {
    return null;
  }

  const previousQuestion = allQuestions[currentQuestionIndex - 1];

  return previousQuestion?.id;
};

// this function returns the id of the previous question of current section in the tc onboarding application
export const getOnboardingPreviousQuestionId = (
  sectionProgresses: Array<SectionProgressDto>,
  questionId?: string,
) => {
  const currentSection = sectionProgresses.find(
    (section) =>
      section.status === 'NOT_STARTED' || section.status === 'IN_PROGRESS',
  );

  const allQuestions = currentSection?.questionProgresses || [];

  const currentQuestionIndex = allQuestions.findIndex(
    (question) => question.id === questionId,
  );

  // No previous question found or the current question is the first one
  if (currentQuestionIndex < 1) {
    return null;
  }

  const previousQuestion = allQuestions[currentQuestionIndex - 1];

  return previousQuestion.id;
};
